import { IMFPlayerConstructor } from '@jsVendor/mediaflow/IMFPlayer';
import { MediaflowBlockElement } from '@jsVendor/mediaflow/IMediaflow';

declare const MFPlayer: IMFPlayerConstructor;

// Stage 2: Mediaflow
export const useVideoBlockMediaflow = () => {
  const jsScriptPath = "https://mfstatic.com/js/mediaflowplayer.min.js";

  // Keep this code in case we need it later
  // When CookieBot consents are changed
  // window.addEventListener('CookiebotOnAccept', () => {
  //   if (window.Cookiebot.consent.marketing) {
  //     document.querySelectorAll('.video-block__content--mediaflow').forEach((element) => {
  //       element.classList.remove('video-block__content--prevent-video');
  //       element.classList.remove('video-block__content--show-video');
  //     });
  //   }
  // });

  // Keep this code in case we need it later
  // Use the shared consent/UI logic
  // useBlockMedia().initializeConsentCheck(
  //   document.querySelectorAll('.video-block__content--mediaflow') as NodeListOf<HTMLElement>,
  // );

  const ensureMediaflowAssetsLoaded = (): Promise<void> => {
    // CSS
    if (!document.querySelector('link[href="https://mfstatic.com/css/mediaflowplayer.min.css"]')) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.type = 'text/css';
      link.href = 'https://mfstatic.com/css/mediaflowplayer.min.css';
      document.head.appendChild(link);
    }

    // JS
    return new Promise<void>((resolve) => {
      if (typeof MFPlayer !== 'undefined') {
        resolve();

        return;
      }
    
      const existingScript = document.querySelector<HTMLScriptElement>(`script[src="${jsScriptPath}"]`);

      // If the script is already being loaded, wait for it to load
      if (existingScript) {
        existingScript.addEventListener('load', () => {
          resolve();
        }, { once: true });
        return;
      }

      loadMediaflowScript().then(() => {
        resolve();
      });
    });
  };

  const loadMediaflowScript = (): Promise<void> => new Promise<void>((resolve, reject) => {
    // Prevent double loading
    if (document.querySelector('script[data-mf-script]')) {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.src = jsScriptPath;
    script.crossOrigin = 'anonymous';
    script.dataset.mfScript = 'true';

    script.onload = () => {
      resolve();
    };

    script.onerror = (err) => {
      console.error('Mediaflow JS failed to load', err);
      reject(err);
    };

    document.body.appendChild(script);
  });

  const initializeVideoAndPlay = async (blockElement: MediaflowBlockElement) => {
    // It does not look like Mediaflow saves any cookies, but keep this code in case we need it later
    // if (!window.Cookiebot?.consent?.marketing) {
    //   return;
    // }

    let player = blockElement._mediaflowPlayer;

    // If the player is not initialized yet
    if (!player) {
      const imageWrapper = blockElement.querySelector('.video-block__image-wrapper') as HTMLElement;
      const container = blockElement.querySelector('.video-block__player-container') as HTMLElement;
      const loadingAlert = blockElement.querySelector('.video-block__hidden-alert') as HTMLDivElement;
      const spinnerElement = blockElement.querySelector('.video-block__loading') as HTMLElement;
      const { videoid } = blockElement.dataset;

      if (!videoid) {
        return;
      }

      if (!container) {
        return;
      }

      blockElement.classList.add('video-block__content--load-video');

      if (spinnerElement) {
        spinnerElement.classList.remove('hidden');
      }

      if (loadingAlert) {
        loadingAlert.textContent = loadingAlert.dataset.text ?? '';
      }

      // Delay to show loading state for better UX
      setTimeout(async function() {
          await ensureMediaflowAssetsLoaded();
          
          // Create the player and let it autoplay when the user has clicked
          // and MFPlayer has initialized in this container.
          player = new MFPlayer(container, videoid, {
            autoPlay: true,
          });
    
          blockElement._mediaflowPlayer = player;
    
          if (imageWrapper) {
            imageWrapper.style.visibility = 'hidden';
          }
    
          blockElement.classList.remove('video-block__content--load-video');
          blockElement.classList.add('video-block__content--show-video');
    
          if (spinnerElement) {
            spinnerElement.classList.add('hidden');
          }
    
          if (loadingAlert) {
            loadingAlert.textContent = '';
          }
    
          blockElement.dataset.initialized = 'true';
      }, 800);
    }
  };

  // Initialize click handlers for Mediaflow blocks
  document.querySelectorAll('.video-block__content--mediaflow').forEach((element) => {
    const blockElement = element as MediaflowBlockElement;

    if (blockElement.dataset.initializedMediaBlockEvents === 'true') {
      return;
    }

    blockElement.addEventListener('click', () => {
      void initializeVideoAndPlay(blockElement);
    });

    blockElement.dataset.initializedMediaBlockEvents = 'true';
  });
};
